import { t } from '@/utils/i18n'
import { useState } from 'react'
import { useSetState, useMount } from 'ahooks'
import { NavBar, List, Button, Toast } from 'antd-mobile'
import dayjs from 'dayjs'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type { PageInfo } from '@/types/interfaces'
import type { OrderDetail } from '../types'
import { apis, utils, cal } from '@/utils'
import { useCartStore, useCorpInfoStore, useCorpSettingStore } from '@/stores'
import type { ProductItem } from '@/types/product'
import OrderDetailItem from '../components/OrderDetailItem'
import OrderAddress from '../components/OrderAddress'
import LogisticsInfo from '../components/LogisticsInfo/LogisticsInfo'
import styles from './Detail.module.less'
import { routesMap, common } from '@/utils'

interface OrderExtendEntityType {
  [key: string]: string | number | null | undefined
}

const { orderApi } = apis
const { getOrderStatusLabel } = common
const { to, toPercentage, cloneDeep } = utils

function OrderDetail() {
  const { cart, setCart } = useCartStore()
  const { cartId } = cart
  const { corpInfo } = useCorpInfoStore()
  const { corpId, pfUserId } = corpInfo
  const { corpSetting } = useCorpSettingStore()
  const { orderId, isShare } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const fromRouterName = searchParams.get('fromRouterName') // 来源路由 name

  const [detail, setDetail] = useSetState<OrderDetail>({
    orderId: '',
    address: '',
    createdTime: '',
    completeStatusTime: '',
    orderNum: '',
    discount: 0,
    discountAmount: 0,
    debt: 0,
    receivable: 0,
    remark: '',
    orderStatus: -1,
    orderPreviewImgUrl: '',
    orderExpressEntities: null,
  })
  const [orderExtendEntity, setOrderExtendEntity] = useSetState<OrderExtendEntityType>({})
  const [productList, setProductList] = useState<ProductItem[]>([])
  const [pageInfo] = useSetState<PageInfo>({
    pageNum: 1,
    pageSize: 1000, // 单笔订单最大货品数量
  })

  const [isShowCancel, setIsShowCancel] = useState(false) // 是否显示取消按钮
  const [orderTotal, setOrderTotal] = useState(0) // 订单总数量
  const [orderMoney, setOrderMoney] = useState(0) // 订单总金额
  const [received, setReceived] = useState(0) // 已收金额
  const [loading, setLoading] = useState(false) // 请求锁

  useMount(() => {
    getOrderdDetail()
    getProductList()
  })

  // 获取订单详情
  async function getOrderdDetail() {
    const { accountId } = corpInfo

    Toast.show({
      icon: 'loading',
      content: 'loading…',
      duration: 0,
    })

    const [err, res] = await to(
      orderApi.saleOrderQuery({
        accountId,
        orderId,
        corpId,
        scan: false, // 是否扫码
        express: !isShare, // 是否查询物流信息
      }),
    )

    Toast.clear()
    if (err) return

    const detailData = res.data || {}
    const { orderExtendEntity } = detailData
    setOrderExtendEntity(orderExtendEntity)
    setIsShowCancel(detailData.source === 2 && (detailData.orderStatus & 2) !== 2)
    setOrderTotal(detailData.productCount)
    setOrderMoney(detailData.receivable)
    setReceived(detailData.received)
    let discount: number | string = 0

    // 计算优惠折扣
    if (detailData.amount === 0) {
      discount = 100
    } else {
      discount = (detailData.amount - detailData.discountAmount) / detailData.amount
    }
    discount = toPercentage(discount)

    setDetail({
      ...detailData,
      customer: `${detailData.customerName} ${detailData.customerCompany}`.trim(),
      phone: detailData.customerPhone,
      address: `${detailData.customerProvince} ${detailData.customerCity} ${detailData.customerDistrict} ${detailData.customerAddress}`,
      amount: detailData.amount,
      receivable: detailData.receivable,
      discount,
      otherReceivable: orderExtendEntity ? orderExtendEntity.otherReceivable : 0,
      debt: cal.minus(detailData.receivable, detailData.received), // 欠款
    })
  }

  // 加载下一页数据
  // async function loadNextPage() {
  //   if (!hasNextPage) return

  //   setPageInfo({ pageNum: pageInfo.pageNum + 1 })
  //   return getProductList()
  // }

  // 单据货品列表查询
  async function getProductList() {
    const params = {
      ...pageInfo,
      orderId,
      corpId,
    }

    const [err, res] = await to(orderApi.queryOrderProductList(params))

    if (err) {
      return
    }

    const newProducts: ProductItem[] = res.data || []
    const { pageNum } = pageInfo

    // 处理价格
    newProducts.forEach((item) => {
      const isMultiPrice = (item.cloudProduct.productStatus & 128) === 128

      if (!isMultiPrice) {
        item.priceText = item.saleProduct.details[0].selectPrice
      } else {
        // 不同规格不同价格
        const { minPrice, maxPrice } = item.saleProduct
        item.priceText = minPrice === maxPrice ? minPrice : `${minPrice}~${maxPrice}`
      }
    })

    setProductList(pageNum === 1 ? newProducts : productList.concat(newProducts))
  }

  // 取消订单
  async function hanldeCancel() {
    if (loading) return

    setLoading(true)
    const [err] = await to(
      orderApi.obsoleteSaleOrder({
        corpId,
        orderId,
        version: detail.sysVersion,
      }),
    )
    setLoading(false)

    if (err) return

    Toast.show({ content: t('取消成功') })

    // 订单取消后隐藏取消按钮
    setIsShowCancel(false)
  }

  // 再次购买
  async function handleReorder() {
    const sendData = {
      corpId,
      orderId,
      cartId,
    }

    if (loading) return
    setLoading(true)
    const [err, res] = await to(orderApi.orderCopy(sendData))
    setLoading(false)

    if (err) return

    Toast.show({ content: t('货品已成功添加到购物车') })
    // 更新 bar 数字
    const cloneCart = cloneDeep(cart)
    cloneCart.productCount = res.data.productCount
    setCart(cloneCart)

    navigate(routesMap.ShoppingCart)
  }

  function handleBack() {
    // 订单成功页面过来的则跳转单据列表
    if (fromRouterName === routesMap.OrderResult) {
      navigate('/order-list', { replace: true })
    } else {
      navigate(-1)
    }
  }

  return (
    <div className={styles['order-detail-container']}>
      <NavBar style={{ backgroundColor: '#fff' }} children={t('单据详情')} onBack={handleBack} />

      <div className={styles['order-detail-wrap']}>
        {/* 订单状态 */}
        <div className={styles['bill-status']}>
          <p className={styles['title']}>{detail.orderId ? getOrderStatusLabel(detail) : '---'}</p>
          <p className={styles['time']}>
            {detail.completeStatusTime
              ? dayjs(Number(detail.completeStatusTime)).format('YYYY-MM-DD HH:mm:ss')
              : '---'}
          </p>
        </div>
        {/* 收货地址 */}
        {/* 待确认和物流配送显示地址 */}
        {[0, 4].includes(Number(orderExtendEntity.distributeType)) && (
          <OrderAddress canEdit={false} pfUserId={pfUserId} backgroundColor={'#fff'} />
        )}

        {/* 物流信息 */}
        {[3, 4].includes(Number(orderExtendEntity.distributeType)) && (
          <LogisticsInfo orderExtendEntity={orderExtendEntity} />
        )}

        {/*  展示订单详情  */}
        <div className={styles['bill-detail-content']}>
          <div className={styles['hd']}>
            {t('货品总计')} ({orderTotal} {t('项')})
          </div>
          {/*  列表数据  */}
          <List style={{ '--border-top': 'none' }}>
            <div className={styles['center']}>
              {productList.map((item, index) => (
                <OrderDetailItem
                  product={item}
                  currencySymbol={corpSetting.currencySymbol}
                  key={item.cloudProduct.productId + index}
                />
              ))}
            </div>
          </List>

          {/* <InfiniteScroll
            hasMore={ hasNextPage }
            finished-text="没有更多了"
            loadMore={ loadNextPage }
            /> */}

          <div className={styles['order-money-info']}>
            {/* 货款 */}
            <div className={styles['cell-group']}>
              <div className={styles['cell-title']}>
                <span>{t('货款')}</span>
              </div>
              <div className={styles['cell-value']}>
                <span>{corpSetting.currencySymbol} {cal.moneyFormat(detail.amount)}</span>
              </div>
            </div>

            {/* 运费 */}
            {orderExtendEntity.factShippingFee ? (<div className={styles['cell-group']}>
              <div className={styles['cell-title']}>
                <span>{t('运费')}</span>
              </div>
              <div className={styles['cell-value']}>
                <span>{corpSetting.currencySymbol} {cal.moneyFormat(Number(orderExtendEntity.factShippingFee) || 0)}</span>
              </div>
            </div>): null}

            {/* COD服务费 */}
            {!!orderExtendEntity.factCodFee && (
              <div className={styles['cell-group']}>
                <div className={styles['cell-title']}>
                  <span>{t('COD 服务费')}</span>
                </div>
                <div className={styles['cell-value']}>
                  <span>{corpSetting.currencySymbol} {cal.moneyFormat(Number(orderExtendEntity.factCodFee) || 0)}</span>
                </div>
              </div>
            )}

            {/* 运费 */}
            {!!orderExtendEntity.vatFee && (
              <div className={styles['cell-group']}>
                <div className={styles['cell-title']}>
                  <span>VAT</span>
                </div>
                <div className={styles['cell-value']}>
                  <span>{corpSetting.currencySymbol} {cal.moneyFormat(Number(orderExtendEntity.vatFee) || 0)}</span>
                </div>
              </div>
            )}
            
            {/* 优惠 */}
            {!!detail.discountAmount && (
              <div className={styles['cell-group']}>
                <div className={styles['cell-title']}>
                  <span>{t('优惠')}</span>
                </div>
                <div className={styles['cell-value']}>
                  <span>-{corpSetting.currencySymbol} {cal.moneyFormat(Number(detail.discountAmount) || 0)}</span>
                </div>
              </div>
            )}

            {/* 实收 */}
            {!!received && (
              <div className={styles['cell-group']}>
                <span className={styles['cell-title']}>{t('实收')} </span>
                <span className={styles['cell-value']}>
                  {corpSetting.currencySymbol} {cal.moneyFormat(received)}
                </span>
              </div>
            )}

            {/* 总计 */}
            <div className={styles['cell-group']}>
              <span className={styles['cell-title']}>{t('总计')} </span>
              <span className={styles['cell-value']}>
                {corpSetting.currencySymbol} {cal.moneyFormat(orderMoney)}
              </span>
            </div>
          </div>

          <div className={styles['order-info']}>
            <div className={styles['order-info-title']}>{t('订单信息')}</div>
            <div className={styles['text-item']}>
              <div className={styles['label']}>{t('门店')}</div>
              <div className={styles['value']}>{detail.storeName}</div>
            </div>
            {detail.orderNum ? (
              <div className={styles['text-item']}>
                <div className={styles['label']}>{t('订单号')}</div>
                <div className={styles['value']}>{detail.orderNum}</div>
              </div>
            ) : (
              ''
            )}
            <div className={styles['text-item']}>
              <div className={styles['label']}>{t('交易时间')}</div>
              <div className={styles['value']}>
                {detail.completeStatusTime
                  ? dayjs(Number(detail.completeStatusTime)).format('YYYY-MM-DD HH:mm')
                  : '---'}
              </div>
            </div>
            <div className={styles['text-item']}>
              <div className={styles['label']}>
                {t('经手人')}
                {/* Handler */}
              </div>
              <div className={styles['value']}>{detail.followName}</div>
            </div>
            {!!detail.remark && (
              <div className={styles['text-item']}>
                <div className={styles['label']}>
                  {t('留言')}
                  {/* Message*/}
                </div>
                <div className={styles['value']}>{detail.remark}</div>
              </div>
            )}
          </div>
        </div>

        <div className={styles['btn-wrap']}>
          <div className={styles['placeholder']}></div>
          <div className={styles['bd']}>
            {isShowCancel && (
              <Button
                color="default"
                className={`${styles['btn-item']} ${styles['cancel']}`}
                loading={loading}
                onClick={hanldeCancel}
              >
                {t('取消')}

                {/* Cancel */}
              </Button>
            )}

            <Button
              color="primary"
              loading={loading}
              className={styles['btn-item']}
              style={{ '--border-width': '0' }}
              onClick={handleReorder}
            >
              {t('再次购买')}

              {/* Reorder */}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderDetail
